import { logger } from "../../utils/logger";
import { useState, useEffect } from "react";
import axios from "axios";
import { Calendar, Image, Trophy, Award, Gift, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardHome() {
  const [stats, setStats] = useState({
    events: 0,
    gallery: 0,
    leaderboard: 0,
    hallOfFame: 0,
    promotions: 0,
    messages: 0,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = sessionStorage.getItem("smsc_token");
        const headers = { Authorization: `Bearer ${token}` };
        
        const [events, gallery, leaderboard, hallOfFame, promotions, messages] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/gallery`),
          axios.get(`${API}/leaderboard`),
          axios.get(`${API}/hall-of-fame`),
          axios.get(`${API}/promotions`),
          axios.get(`${API}/contact/submissions`, { headers }).catch(() => ({ data: [] })),
        ]);

        setStats({
          events: events.data.length,
          gallery: gallery.data.length,
          leaderboard: leaderboard.data.length,
          hallOfFame: hallOfFame.data.length,
          promotions: promotions.data.length,
          messages: messages.data.filter(m => !m.is_read).length,
        });
      } catch (err) {
        logger.error("Error fetching stats:", err);
      }
    };
    fetchStats();
  }, []);

  const statCards = [
    { label: "Events", value: stats.events, icon: Calendar, color: "text-blue-400" },
    { label: "Gallery Images", value: stats.gallery, icon: Image, color: "text-green-400" },
    { label: "Leaderboard", value: stats.leaderboard, icon: Trophy, color: "text-gold" },
    { label: "Hall of Fame", value: stats.hallOfFame, icon: Award, color: "text-purple-400" },
    { label: "Promotions", value: stats.promotions, icon: Gift, color: "text-pink-400" },
    { label: "Unread Messages", value: stats.messages, icon: MessageSquare, color: "text-red-400" },
  ];

  return (
    <div data-testid="dashboard-home">
      <div className="mb-8">
        <h1 className="font-heading text-3xl text-white mb-2">Dashboard</h1>
        <p className="text-white/60">Welcome back! Here's an overview of your content.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat) => (
          <div
            key={stat.label}
            className="glass-card p-6 hover:border-gold/30 transition-colors"
            data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm mb-1">{stat.label}</p>
                <p className="font-heading text-4xl text-white">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center ${stat.color}`}>
                <stat.icon className="w-6 h-6" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 glass-card p-6">
        <h2 className="font-heading text-xl text-white mb-4">Quick Tips</h2>
        <ul className="space-y-3 text-white/60">
          <li className="flex items-start gap-2">
            <span className="text-gold">•</span>
            <span>Use the <strong className="text-white">Events Manager</strong> to update your weekly tournament schedule.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gold">•</span>
            <span>Upload photos to the <strong className="text-white">Gallery</strong> to showcase your club's atmosphere.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gold">•</span>
            <span>Keep the <strong className="text-white">Leaderboard</strong> updated to engage competitive players.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gold">•</span>
            <span>Check <strong className="text-white">Messages</strong> regularly for customer inquiries.</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
